// Bootstrap: login gate, then the light shell (user badge, workspaces, the
// field app), then — unless the field app is what this session opened — the
// heavy half (fullApp.ts: map, geometry, panels, packages, dashboard ...).
//
// Field boot (2026-10-05, the Christchurch mobile-data test): an admin has
// every workspace, so the old "skip the geometry only when ALL workspaces
// are field ones" rule still downloaded ~20 MB (geometry, /api/pipes,
// dashboard, package hulls, share / asset-issue polling) on a phone that
// only wanted the field app, and a refresh over a weak link hung. Now the
// field workspace being the CURRENT one is enough: nothing network-wide is
// requested, and fullApp.ts (its own chunk) is not even downloaded, until
// the user switches to a non-field workspace or taps "Load the full app".
import { initAdmin } from "./admin";
import { checkAuth, initUserBadge, showLoginScreen } from "./auth";
import { logout } from "./api";
import { initPresentationMode } from "./presentationMask";
import { ensureTermsAccepted, initTerms } from "./terms";
import { isGisOnly, loadDataProfile, renderDataProfileBadge } from "./dataProfile";
import { isMobileViewport } from "./mobileNav";
import { initNotificationBell } from "./notificationsInbox";
import { initMyWork } from "./myWork";
import { initApprovals } from "./approvalsInbox";
import { initOrgSwitcher } from "./orgSwitcher";
import { getState } from "./state";
import { showToast } from "./toast";
import { applyWorkspace, currentWorkspace, initWorkspaces, storedWorkspace, workspaceChoices } from "./workspaces";
import { fieldBootWorkspace } from "./field/fieldBoot";
import { initFieldApp, isFieldWorkspace } from "./field/fieldApp";
import { initFieldReview } from "./field/fieldReview";
import { markBooted, registerServiceWorker, reloadForStaleChunk } from "./field/pwa";
import { hideBootError, offlineText, showBootError } from "./bootError";
import { withRetry } from "./bootRetry";
import { initPageFeedback } from "./pageFeedback";
import { openFeedbackDeepLink } from "./feedbackRegister";

const WIDE_TABS = new Set(["deliveryBoard", "myProjects", "fieldReview", "team"]);
// Briefs keeps the map usable (polygon drawing, "Show on map").
// tenderMap (procurement.ts): the inferred tender packs are reviewed on the map.
// assetIssues: the "Open asset issues" map layer + lasso are part of triage (2026-10-05).
const MEDIUM_TABS = new Set(["briefs", "tenderMap", "assetIssues", "myWork", "approvals"]);

function initSidebarTabs(): void {
  document.querySelectorAll<HTMLButtonElement>("#sidebar [data-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("#sidebar .tab-btn").forEach((b) => b.classList.toggle("active", b === btn));
      // Board / report / admin tabs are tables and charts, not map tools:
      // give them a wide sidebar (app.css #app.wide-tab) instead of 280px.
      const app = document.getElementById("app");
      const tab = btn.dataset.tab ?? "";
      app?.classList.toggle("wide-tab", WIDE_TABS.has(tab) || tab.startsWith("exec") || tab.startsWith("admin") || tab.startsWith("proc"));
      app?.classList.toggle("medium-tab", MEDIUM_TABS.has(tab));
      document
        .querySelectorAll("#sidebar .tab-panel")
        .forEach((p) => p.classList.toggle("active", p.id === `tab-${btn.dataset.tab}`));
    });
  });
}

let gisOnly = false;
let fullApp: Promise<void> | null = null;

/** The heavy chunk, retried; a chunk the server no longer has (a deploy
 * replaced dist/ under a page from an older build) reloads onto the new
 * build once (pwa.reloadForStaleChunk). */
async function importFullApp(): Promise<typeof import("./fullApp")> {
  try {
    return await withRetry(async () => {
      const m = await import("./fullApp");
      if (!m?.runMapApp) throw new Error("the full app chunk didn't load"); // never boot half a module
      return m;
    }, { attempts: 3, baseMs: 600 });
  } catch (e) {
    if (navigator.onLine) await reloadForStaleChunk();
    throw e;
  }
}

/** Load the heavy half once (re-entrant: later calls share the promise).
 * Its data modules initialise synchronously after the chunk arrives; the
 * map boot then runs in the background. */
function ensureFullApp(): Promise<void> {
  if (!fullApp) {
    // from the field app (a workspace switch / "Load the full app"): a failure
    // just toasts and the field app carries on; at boot it is the boot error
    const fromField = document.body.classList.contains("field-lite");
    document.body.classList.remove("field-lite");
    const caption = document.getElementById("status-caption");
    if (caption) caption.textContent = "Loading the full app…";
    fullApp = importFullApp()
      .then(async (m) => {
        m.initDataModules(gisOnly);
        // The switch that triggered this already clicked the new workspace's
        // landing tab, before the modules above wired their tab handlers:
        // land again so e.g. the dashboard loads. (Not when "Load the full
        // app" was tapped inside a field workspace: that would reopen it.)
        const ws = currentWorkspace();
        if (!isFieldWorkspace(ws)) applyWorkspace(ws, true);
        await m.runMapApp(gisOnly);
      })
      .catch((err) => {
        fullApp = null; // offline: let a later switch try again
        if (!fromField) throw err; // runAppSafely: "couldn't start" + Retry
        document.body.classList.add("field-lite");
        console.error(err);
        showToast("Couldn't load the full app (no signal?). The field app still works.");
      });
  }
  return fullApp;
}

/** The map area while the heavy half isn't loaded: say why, offer to load. */
function showFieldLitePlaceholder(): void {
  const host = document.getElementById("map-container");
  if (!host || host.querySelector(".field-lite-placeholder")) return;
  const box = document.createElement("div");
  box.className = "field-lite-placeholder";
  const p = document.createElement("p");
  p.textContent =
    "Field mode: the network map, packages and dashboard aren't loaded, to keep mobile data low. They load when you switch to a non-field workspace.";
  const btn = document.createElement("button");
  btn.className = "fa-btn";
  btn.textContent = "Load the full app";
  btn.addEventListener("click", () => void ensureFullApp());
  box.append(p, btn);
  host.append(box);
}

async function runApp(): Promise<void> {
  initUserBadge();
  // right-click "Give feedback on this page…" everywhere + the Feedback link
  initPageFeedback();
  // "Viewing as <org>" banner + the platform-admin organisation dropdown.
  void initOrgSwitcher();
  // Persistent notifications inbox + profile popover (every organisation).
  initNotificationBell();
  // Presentation / demo mode (presentationMask.ts): header toggle for
  // managers+, and the on-screen mask when this session has it on.
  void initPresentationMode();
  // Data profile first (P7a): a GIS-only organisation must not fire any of
  // the pipeline-data requests below — the backend 404s them. For a full
  // profile (client 1) nothing below changes; a failed call assumes full.
  await loadDataProfile();
  gisOnly = isGisOnly();
  if (gisOnly) document.body.classList.add("gis-only");

  const choices = workspaceChoices(getState().currentUser);
  const fieldStart = fieldBootWorkspace(choices, location.hash, storedWorkspace(), isMobileViewport());
  // Normal boot: fetch the heavy chunk now so its data modules (dashboard,
  // briefs, ...) set up their tabs before the workspace is applied, as before.
  const full = fieldStart ? null : await importFullApp();
  full?.initDataModules(gisOnly);
  // Tab wiring + workspace (which tabs this user sees) up front, so the
  // sidebar already shows the right view during the slow geometry load.
  initSidebarTabs();
  initAdmin();
  // My work dashboards (every workspace): before initWorkspaces, whose
  // landing click renders the page.
  initMyWork();
  // Managers' Approvals inbox (manager+; hides its tab for everyone else).
  initApprovals();
  // Field workspaces (2026-10-02): before initWorkspaces so the landing
  // "Field" tab click opens the full-screen field app straight away.
  initFieldApp();
  initFieldReview();
  initWorkspaces(fieldStart);
  renderDataProfileBadge();
  // #feedback=<id> (the WRAP board's link): Admin > Feedback on that item
  openFeedbackDeepLink();

  if (fieldStart && isFieldWorkspace(currentWorkspace())) {
    document.body.classList.add("field-lite");
    // A crew member whose only workspaces are Construction / Operations
    // never has a main app to go back to.
    if (choices.every((w) => isFieldWorkspace(w))) document.body.classList.add("field-only");
    showFieldLitePlaceholder();
    // Switching to any other workspace loads the rest. The listener runs
    // inside workspaces.ts's dispatch, before it clicks the new landing tab.
    document.addEventListener("workspace-changed", (e) => {
      const ws = (e as CustomEvent).detail?.workspace as string;
      if (!isFieldWorkspace(ws)) void ensureFullApp();
    });
    document.addEventListener("wcc-full-app-needed", () => void ensureFullApp());
    return;
  }
  fullApp = full ? full.runMapApp(gisOnly) : ensureFullApp();
  await fullApp;
}

/** runApp, but a failure shows "couldn't start" + Retry instead of a black
 * screen (2026-10-08, wraptest.nz). Retry reloads: half-initialised modules
 * can't be re-run safely. */
async function runAppSafely(): Promise<void> {
  try {
    await runApp();
    markBooted();
  } catch (err) {
    console.error("boot failed", err);
    showBootError({
      title: "WRAP couldn't start",
      message: `Part of the app didn't load. ${offlineText()}`,
      retry: async () => {
        window.location.reload();
        await new Promise<void>(() => undefined);
      },
    });
  }
}

/** /api/auth/me couldn't be reached (not a 401): keep the session, offer Retry. */
function showUnreachable(): Promise<void> {
  return new Promise<void>((resolve) => {
    showBootError({
      title: "Can't reach WRAP",
      message: offlineText(),
      retry: async () => {
        const again = await checkAuth();
        if (again === "unreachable") throw new Error("still unreachable");
        hideBootError();
        if (again === "authed") {
          await ensureTermsAccepted();
          await runAppSafely();
        } else {
          showLoginScreen(() => void ensureTermsAccepted().then(() => runAppSafely()));
        }
        resolve();
      },
      secondary: { label: "Sign in again", run: () => void logout().catch(() => undefined).finally(() => window.location.reload()) },
    });
  });
}

async function bootstrap(): Promise<void> {
  registerServiceWorker();
  initTerms();
  const auth = await checkAuth();
  if (auth === "authed") {
    // Terms of use (terms.ts): blocking until the current version is
    // accepted (the backend 403s every data route until then), plus the
    // once-per-login reminder (deferred, never blocking, when its text
    // can't be loaded). Nothing in runApp fires before this resolves.
    await ensureTermsAccepted();
    await runAppSafely();
    return;
  }
  if (auth === "unreachable") {
    await showUnreachable();
    return;
  }
  showLoginScreen(() => {
    void ensureTermsAccepted().then(() => runAppSafely());
  });
}

void bootstrap();
